import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, Trash2, FolderPlus, Image, LogOut, ChevronDown, ChevronUp, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioAdminPage() {
  const [auth, setAuth] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [uploading, setUploading] = useState({});

  const authHeader = auth ? { Authorization: `Basic ${btoa(`${auth.email}:${auth.password}`)}` } : {};

  const fetchCategories = useCallback(async () => {
    if (!auth) return;
    try {
      const res = await axios.get(`${API}/portfolio/categories`, { headers: authHeader });
      setCategories(res.data.categories);
    } catch {
      toast.error("Failed to load categories");
    }
  }, [auth]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    axios.get(`${API}/portfolio/categories`, {
      headers: { Authorization: `Basic ${btoa(`${email}:${password}`)}` }
    })
      .then((res) => {
        setAuth({ email, password });
        setCategories(res.data.categories);
        toast.success("Logged in");
      })
      .catch(() => toast.error("Invalid credentials"))
      .finally(() => setLoading(false));
  };

  const createCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const form = new FormData();
      form.append("name", newCatName);
      await axios.post(`${API}/portfolio/categories`, form, { headers: authHeader });
      setNewCatName("");
      toast.success("Category created");
      fetchCategories();
    } catch {
      toast.error("Failed to create category");
    }
  };

  const deleteCategory = async (slug) => {
    if (!window.confirm(`Delete "${slug}" and all its photos?`)) return;
    try {
      await axios.delete(`${API}/portfolio/categories/${slug}`, { headers: authHeader });
      toast.success("Category deleted");
      fetchCategories();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const uploadPhotos = async (slug, files) => {
    setUploading((p) => ({ ...p, [slug]: true }));
    try {
      const form = new FormData();
      for (const file of files) form.append("files", file);
      await axios.post(`${API}/portfolio/upload/${slug}`, form, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} photo(s) uploaded`);
      fetchCategories();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading((p) => ({ ...p, [slug]: false }));
    }
  };

  const deletePhoto = async (slug, filename) => {
    try {
      await axios.delete(`${API}/portfolio/${slug}/${filename}`, { headers: authHeader });
      toast.success("Photo deleted");
      fetchCategories();
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              VAN <span className="gold-shimmer">SIGN</span>
            </h1>
            <p className="text-white/40 text-sm">Portfolio Admin</p>
          </div>
          <form onSubmit={handleLogin} className="glass-card rounded-2xl p-8 space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              data-testid="admin-email"
              className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 outline-none"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password"
              className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-gold text-black font-bold py-3 rounded-full text-sm disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419] py-8 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">
              VAN <span className="gold-shimmer">SIGN</span>
            </h1>
            <p className="text-white/40 text-sm">Portfolio Manager</p>
          </div>
          <button onClick={() => setAuth(null)} className="flex items-center gap-2 text-white/50 hover:text-white text-sm">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        {/* Add Category */}
        <div className="glass-card rounded-xl p-5 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <FolderPlus className="w-4 h-4 text-gold" />
            <span className="text-white text-sm font-medium">New Category</span>
          </div>
          <input
            type="text"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="Category name (e.g. Vehicle Wraps)"
            data-testid="new-cat-input"
            className="flex-1 bg-black/50 border border-white/10 text-white rounded-lg px-3 py-2 text-sm placeholder:text-white/20 outline-none"
            onKeyDown={(e) => e.key === "Enter" && createCategory()}
          />
          <button onClick={createCategory} data-testid="create-cat-btn" className="bg-gold text-black font-bold px-5 py-2 rounded-full text-sm flex-shrink-0">
            Create
          </button>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          {categories.length === 0 && (
            <p className="text-white/30 text-center py-12">No categories yet</p>
          )}
          {categories.map((cat) => (
            <div key={cat.slug} className="glass-card rounded-xl overflow-hidden">
              {/* Category Header with Thumbnail */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                onClick={() => setExpandedCat(expandedCat === cat.slug ? null : cat.slug)}
              >
                {/* Thumbnail */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
                  {cat.photos.length > 0 ? (
                    <img src={`/images/portfolio/${cat.slug}/${cat.photos[0]}`} alt={cat.slug} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-white/20" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-white font-medium text-sm block">{cat.slug}</span>
                  <span className="text-white/30 text-xs">{cat.photo_count} photos</span>
                </div>
                <div className="flex items-center gap-2">
                  <label
                    className="bg-white/5 border border-white/10 hover:border-gold/30 text-white/60 hover:text-gold px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Upload className="w-3 h-3" />
                    {uploading[cat.slug] ? "Uploading..." : "Upload"}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files.length > 0 && uploadPhotos(cat.slug, e.target.files)}
                    />
                  </label>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteCategory(cat.slug); }}
                    className="text-red-400/60 hover:text-red-400 p-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  {expandedCat === cat.slug ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                </div>
              </div>

              {/* Photos Grid */}
              {expandedCat === cat.slug && (
                <div className="px-5 pb-5 border-t border-white/5 pt-4">
                  {cat.photos.length === 0 ? (
                    <p className="text-white/20 text-xs text-center py-6">No photos in this category</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {cat.photos.map((photo) => (
                        <div key={photo} className="relative group rounded-lg overflow-hidden aspect-square">
                          <img
                            src={`/images/portfolio/${cat.slug}/${photo}`}
                            alt={photo}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <button
                            onClick={() => deletePhoto(cat.slug, photo)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5">
                            <span className="text-white/60 text-[10px]">{photo}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
